export const TRAVERSAL_WORLD_VERSION = 1;

export const TRAVERSAL_VOLUME_KINDS = Object.freeze([
  'solid',
  'water',
  'climbable',
  'crawl-under',
  'cover',
  'hazard',
]);

const EPSILON = 1e-7;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeTraversalWorldChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function compareText(left, right) {
  return left < right ? -1 : left > right ? 1 : 0;
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function normalizePosition(value = {}, name = 'position') {
  assertPlainObject(value, name);
  return {
    x: assertFinite(value.x ?? 0, `${name}.x`),
    y: assertFinite(value.y ?? 0, `${name}.y`),
    z: assertFinite(value.z ?? 0, `${name}.z`),
  };
}

function normalizeBounds(value) {
  assertPlainObject(value, 'bounds');
  const min = normalizePosition(value.min, 'bounds.min');
  const max = normalizePosition(value.max, 'bounds.max');
  for (const axis of ['x', 'y', 'z']) {
    if (min[axis] >= max[axis]) {
      throw new RangeError(`bounds.min.${axis} must be less than bounds.max.${axis}`);
    }
  }
  return { min, max };
}

function normalizeJson(value, name = 'value') {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return assertFinite(value, name);
  if (Array.isArray(value)) {
    return value.map((entry, index) => normalizeJson(entry, `${name}[${index}]`));
  }
  assertPlainObject(value, name);
  const normalized = {};
  for (const key of Object.keys(value).sort()) {
    if (value[key] === undefined) throw new TypeError(`${name}.${key} cannot be undefined`);
    normalized[key] = normalizeJson(value[key], `${name}.${key}`);
  }
  return normalized;
}

function normalizeKind(value) {
  const kind = assertNonEmptyString(value, 'kind');
  if (!TRAVERSAL_VOLUME_KINDS.includes(kind)) {
    throw new RangeError(`Unsupported traversal volume kind: ${kind}`);
  }
  return kind;
}

function normalizeMetadata(kind, metadata, bounds) {
  const normalized = normalizeJson(metadata ?? {}, 'metadata');
  assertPlainObject(normalized, 'metadata');
  if (kind === 'crawl-under') {
    normalized.clearance = assertPositiveFinite(
      normalized.clearance ?? bounds.max.y - bounds.min.y,
      'metadata.clearance',
    );
    normalized.requiresCrawl = normalized.requiresCrawl === undefined
      ? true
      : Boolean(normalized.requiresCrawl);
  }
  if (kind === 'climbable') {
    normalized.surfaceY = assertFinite(normalized.surfaceY ?? bounds.max.y, 'metadata.surfaceY');
    normalized.requiresClimb = normalized.requiresClimb === undefined
      ? true
      : Boolean(normalized.requiresClimb);
  }
  if (kind === 'water') {
    normalized.surfaceY = assertFinite(normalized.surfaceY ?? bounds.max.y, 'metadata.surfaceY');
  }
  if (kind === 'hazard') {
    normalized.damagePerSecond = assertFiniteNonNegative(
      normalized.damagePerSecond ?? 0,
      'metadata.damagePerSecond',
    );
  }
  return normalized;
}

function buildVolume({ id, kind, bounds, metadata, revision }) {
  const normalizedKind = normalizeKind(kind);
  const normalizedBounds = normalizeBounds(bounds);
  return deepFreeze({
    version: TRAVERSAL_WORLD_VERSION,
    id: assertNonEmptyString(id, 'id'),
    kind: normalizedKind,
    bounds: normalizedBounds,
    metadata: normalizeMetadata(normalizedKind, metadata, normalizedBounds),
    revision: assertPositiveInteger(revision, 'revision'),
  });
}

function normalizeCapabilities(value = {}) {
  assertPlainObject(value, 'capabilities');
  return {
    swim: Boolean(value.swim),
    climb: Boolean(value.climb),
    crawl: Boolean(value.crawl),
  };
}

function verticalOverlap(positionY, height, bounds) {
  const actorMin = positionY;
  const actorMax = positionY + height;
  return actorMax > bounds.min.y + EPSILON && actorMin < bounds.max.y - EPSILON;
}

function circleIntersectsBounds(position, radius, bounds) {
  const closestX = Math.max(bounds.min.x, Math.min(position.x, bounds.max.x));
  const closestZ = Math.max(bounds.min.z, Math.min(position.z, bounds.max.z));
  const dx = position.x - closestX;
  const dz = position.z - closestZ;
  return (dx * dx) + (dz * dz) <= (radius * radius) + EPSILON;
}

function volumeBlocksActor(volume, profile) {
  if (volume.kind === 'solid') return true;
  if (volume.kind === 'water') return !profile.capabilities.swim;
  if (volume.kind === 'climbable') {
    return volume.metadata.requiresClimb && !profile.capabilities.climb;
  }
  if (volume.kind === 'crawl-under') {
    return (volume.metadata.requiresCrawl && !profile.capabilities.crawl)
      || profile.height > volume.metadata.clearance + EPSILON;
  }
  return false;
}

function segmentEntry(from, to, volume, radius, height) {
  const bounds = volume.bounds;
  const minY = Math.min(from.y, to.y);
  const maxY = Math.max(from.y + height, to.y + height);
  if (maxY <= bounds.min.y + EPSILON || minY >= bounds.max.y - EPSILON) return null;

  const minX = bounds.min.x - radius;
  const maxX = bounds.max.x + radius;
  const minZ = bounds.min.z - radius;
  const maxZ = bounds.max.z + radius;
  const startsInside = from.x >= minX && from.x <= maxX && from.z >= minZ && from.z <= maxZ;
  const endsInside = to.x >= minX && to.x <= maxX && to.z >= minZ && to.z <= maxZ;
  if (startsInside) {
    if (!endsInside) return null;
    return { time: 0, normal: { x: 0, z: 0 } };
  }

  const dx = to.x - from.x;
  const dz = to.z - from.z;
  let entry = 0;
  let exit = 1;
  let normal = { x: 0, z: 0 };

  const axes = [
    { delta: dx, start: from.x, min: minX, max: maxX, axis: 'x' },
    { delta: dz, start: from.z, min: minZ, max: maxZ, axis: 'z' },
  ];

  for (const axis of axes) {
    if (Math.abs(axis.delta) <= EPSILON) {
      if (axis.start < axis.min || axis.start > axis.max) return null;
      continue;
    }
    const first = (axis.min - axis.start) / axis.delta;
    const second = (axis.max - axis.start) / axis.delta;
    const near = Math.min(first, second);
    const far = Math.max(first, second);
    if (near > entry) {
      entry = near;
      normal = axis.axis === 'x'
        ? { x: axis.delta > 0 ? -1 : 1, z: 0 }
        : { x: 0, z: axis.delta > 0 ? -1 : 1 };
    }
    exit = Math.min(exit, far);
    if (entry - exit > EPSILON) return null;
  }

  if (entry < -EPSILON || entry > 1 + EPSILON) return null;
  return { time: Math.max(0, Math.min(1, entry)), normal };
}

function interpolate(from, to, time) {
  return {
    x: from.x + ((to.x - from.x) * time),
    y: from.y + ((to.y - from.y) * time),
    z: from.z + ((to.z - from.z) * time),
  };
}

function distance(left, right) {
  return Math.hypot(left.x - right.x, left.y - right.y, left.z - right.z);
}

function normalizeStringArray(value, name) {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`);
  const normalized = value.map((entry, index) => assertNonEmptyString(entry, `${name}[${index}]`));
  if (new Set(normalized).size !== normalized.length) {
    throw new RangeError(`${name} cannot contain duplicates`);
  }
  return normalized.sort(compareText);
}

function buildActorState({
  actorId,
  actorKind,
  revision,
  position,
  mode,
  blocked,
  inWater,
  onClimbable,
  hidden,
  collisionVolumeIds,
  contactVolumeIds,
  hazardVolumeIds,
  tick,
  simulationTime,
  stepSeconds,
}) {
  const normalizedMode = assertNonEmptyString(mode, 'mode');
  if (!['ground', 'water', 'climb', 'crawl'].includes(normalizedMode)) {
    throw new RangeError(`Unsupported traversal mode: ${normalizedMode}`);
  }
  return deepFreeze({
    version: TRAVERSAL_WORLD_VERSION,
    actorId: assertNonEmptyString(actorId, 'actorId'),
    actorKind: assertNonEmptyString(actorKind, 'actorKind'),
    revision: assertPositiveInteger(revision, 'revision'),
    position: normalizePosition(position),
    mode: normalizedMode,
    blocked: Boolean(blocked),
    inWater: Boolean(inWater),
    onClimbable: Boolean(onClimbable),
    hidden: Boolean(hidden),
    collisionVolumeIds: normalizeStringArray(collisionVolumeIds, 'collisionVolumeIds'),
    contactVolumeIds: normalizeStringArray(contactVolumeIds, 'contactVolumeIds'),
    hazardVolumeIds: normalizeStringArray(hazardVolumeIds, 'hazardVolumeIds'),
    tick: tick === null ? null : assertNonNegativeInteger(tick, 'tick'),
    simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
    stepSeconds: stepSeconds === null ? null : assertPositiveFinite(stepSeconds, 'stepSeconds'),
  });
}

export class TraversalWorld {
  constructor() {
    this.volumes = new Map();
    this.actorStates = new Map();
    this.revision = 0;
    this.nextVolumeNumber = 1;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
  }

  addVolume(definition) {
    assertPlainObject(definition, 'definition');
    if (definition.revision !== undefined || definition.version !== undefined) {
      throw new RangeError('volume version and revision are authoritative');
    }
    const explicitId = definition.id === undefined
      ? null
      : assertNonEmptyString(definition.id, 'definition.id');
    let id = explicitId;
    let nextVolumeNumber = this.nextVolumeNumber;
    if (id === null) {
      do {
        id = `volume-${String(nextVolumeNumber).padStart(6, '0')}`;
        nextVolumeNumber += 1;
      } while (this.volumes.has(id));
    } else {
      if (this.volumes.has(id)) throw new RangeError(`Traversal volume already exists: ${id}`);
      const match = /^volume-(\d+)$/.exec(id);
      if (match) {
        const number = Number(match[1]);
        if (Number.isSafeInteger(number)) nextVolumeNumber = Math.max(nextVolumeNumber, number + 1);
      }
    }
    const volume = buildVolume({
      id,
      kind: definition.kind,
      bounds: definition.bounds,
      metadata: definition.metadata ?? {},
      revision: this.revision + 1,
    });
    this.volumes.set(id, volume);
    this.actorStates = new Map();
    this.nextVolumeNumber = nextVolumeNumber;
    this.revision += 1;
    return volume;
  }

  removeVolume(id) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const volume = this.volumes.get(normalizedId) ?? null;
    if (!volume) return null;
    this.volumes.delete(normalizedId);
    this.actorStates = new Map();
    this.revision += 1;
    return volume;
  }

  getVolume(id) {
    return this.volumes.get(assertNonEmptyString(id, 'id')) ?? null;
  }

  listVolumes({ kind = undefined } = {}) {
    const normalizedKind = kind === undefined ? null : normalizeKind(kind);
    return [...this.volumes.values()]
      .filter((volume) => normalizedKind === null || volume.kind === normalizedKind)
      .sort((left, right) => compareText(left.id, right.id));
  }

  queryPoint({ point, radius = 0, height = 0, kinds = undefined }) {
    const normalizedPoint = normalizePosition(point, 'point');
    const normalizedRadius = assertFiniteNonNegative(radius, 'radius');
    const normalizedHeight = assertFiniteNonNegative(height, 'height');
    const kindSet = kinds === undefined
      ? null
      : new Set(normalizeStringArray(kinds, 'kinds').map(normalizeKind));
    return this.listVolumes()
      .filter((volume) => kindSet === null || kindSet.has(volume.kind))
      .filter((volume) => circleIntersectsBounds(normalizedPoint, normalizedRadius, volume.bounds))
      .filter((volume) => normalizedHeight === 0
        ? normalizedPoint.y >= volume.bounds.min.y - EPSILON
          && normalizedPoint.y <= volume.bounds.max.y + EPSILON
        : verticalOverlap(normalizedPoint.y, normalizedHeight, volume.bounds));
  }

  resolveMovement({
    actorId,
    actorKind,
    from,
    to,
    radius,
    height,
    capabilities = {},
  }) {
    const profile = {
      actorId: assertNonEmptyString(actorId, 'actorId'),
      actorKind: assertNonEmptyString(actorKind, 'actorKind'),
      from: normalizePosition(from, 'from'),
      to: normalizePosition(to, 'to'),
      radius: assertPositiveFinite(radius, 'radius'),
      height: assertPositiveFinite(height, 'height'),
      capabilities: normalizeCapabilities(capabilities),
    };
    const blocking = this.listVolumes().filter((volume) => volumeBlocksActor(volume, profile));

    const sweep = (start, target) => {
      let best = null;
      for (const volume of blocking) {
        const hit = segmentEntry(start, target, volume, profile.radius, profile.height);
        if (!hit) continue;
        if (!best
          || hit.time < best.time - EPSILON
          || (Math.abs(hit.time - best.time) <= EPSILON && compareText(volume.id, best.volume.id) < 0)) {
          best = { ...hit, volume };
        }
      }
      return best;
    };

    const collisions = [];
    let position = profile.to;
    const first = sweep(profile.from, profile.to);
    if (first) {
      collisions.push(first.volume.id);
      const safeTime = Math.max(0, first.time - EPSILON);
      const safe = interpolate(profile.from, profile.to, safeTime);
      if (first.normal.x === 0 && first.normal.z === 0) {
        position = profile.from;
      } else {
        const remaining = {
          x: profile.to.x - safe.x,
          y: profile.to.y - safe.y,
          z: profile.to.z - safe.z,
        };
        if (first.normal.x !== 0) remaining.x = 0;
        if (first.normal.z !== 0) remaining.z = 0;
        const slideTarget = {
          x: safe.x + remaining.x,
          y: safe.y + remaining.y,
          z: safe.z + remaining.z,
        };
        const second = sweep(safe, slideTarget);
        if (second) {
          collisions.push(second.volume.id);
          position = interpolate(safe, slideTarget, Math.max(0, second.time - EPSILON));
        } else {
          position = slideTarget;
        }
      }
    }

    const contacts = this.listVolumes().filter((volume) => (
      circleIntersectsBounds(position, profile.radius, volume.bounds)
      && verticalOverlap(position.y, profile.height, volume.bounds)
    ));
    const contactVolumeIds = contacts.map((volume) => volume.id).sort(compareText);
    const water = contacts.filter((volume) => volume.kind === 'water');
    const climbable = contacts
      .filter((volume) => volume.kind === 'climbable' && !volumeBlocksActor(volume, profile))
      .sort((left, right) => (
        right.metadata.surfaceY - left.metadata.surfaceY || compareText(left.id, right.id)
      ));
    const crawlUnder = contacts.filter((volume) => (
      volume.kind === 'crawl-under' && !volumeBlocksActor(volume, profile)
    ));
    const cover = contacts.filter((volume) => volume.kind === 'cover');
    const hazards = contacts.filter((volume) => volume.kind === 'hazard');

    if (climbable.length > 0) position = { ...position, y: climbable[0].metadata.surfaceY };
    const inWater = water.length > 0 && profile.capabilities.swim;
    const onClimbable = climbable.length > 0;
    const underCover = cover.length > 0 || crawlUnder.length > 0;
    const mode = crawlUnder.length > 0
      ? 'crawl'
      : onClimbable
        ? 'climb'
        : inWater
          ? 'water'
          : 'ground';

    return deepFreeze({
      version: TRAVERSAL_WORLD_VERSION,
      actorId: profile.actorId,
      actorKind: profile.actorKind,
      from: profile.from,
      requestedPosition: profile.to,
      position,
      radius: profile.radius,
      height: profile.height,
      capabilities: profile.capabilities,
      blocked: collisions.length > 0,
      collisionVolumeIds: [...new Set(collisions)].sort(compareText),
      contactVolumeIds,
      hazardVolumeIds: hazards.map((volume) => volume.id).sort(compareText),
      mode,
      inWater,
      onClimbable,
      hidden: underCover,
      requestedDistance: distance(profile.from, profile.to),
      resolvedDistance: distance(profile.from, position),
    });
  }

  commitActorResolution(resolution, {
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
    stepSeconds = this.currentStep.stepSeconds,
  } = {}) {
    assertPlainObject(resolution, 'resolution');
    if (resolution.version !== TRAVERSAL_WORLD_VERSION) {
      throw new RangeError(`Unsupported traversal resolution version: ${resolution.version}`);
    }
    const normalizedTick = tick === null ? null : assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedStepSeconds = stepSeconds === null
      ? null
      : assertPositiveFinite(stepSeconds, 'stepSeconds');
    const referencedVolumeIds = [
      ...(resolution.collisionVolumeIds ?? []),
      ...(resolution.contactVolumeIds ?? []),
      ...(resolution.hazardVolumeIds ?? []),
    ];
    for (const id of referencedVolumeIds) {
      if (!this.volumes.has(assertNonEmptyString(id, 'resolution volume ID'))) {
        throw new RangeError(`Traversal resolution references missing volume: ${id}`);
      }
    }
    const previous = this.actorStates.get(resolution.actorId);
    const state = buildActorState({
      actorId: resolution.actorId,
      actorKind: resolution.actorKind,
      revision: (previous?.revision ?? 0) + 1,
      position: resolution.position,
      mode: resolution.mode,
      blocked: resolution.blocked,
      inWater: resolution.inWater,
      onClimbable: resolution.onClimbable,
      hidden: resolution.hidden,
      collisionVolumeIds: resolution.collisionVolumeIds,
      contactVolumeIds: resolution.contactVolumeIds,
      hazardVolumeIds: resolution.hazardVolumeIds,
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    });
    this.actorStates.set(state.actorId, state);
    this.revision += 1;
    this.currentStep = {
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    };
    return state;
  }

  syncActor({
    actorId,
    actorKind,
    position,
    radius,
    height,
    capabilities = {},
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
    stepSeconds = this.currentStep.stepSeconds,
  }) {
    const resolution = this.resolveMovement({
      actorId,
      actorKind,
      from: position,
      to: position,
      radius,
      height,
      capabilities,
    });
    return this.commitActorResolution(resolution, { tick, simulationTime, stepSeconds });
  }

  getActorState(actorId) {
    return this.actorStates.get(assertNonEmptyString(actorId, 'actorId')) ?? null;
  }

  listActorStates({ actorKind = undefined, mode = undefined } = {}) {
    const normalizedKind = actorKind === undefined ? null : assertNonEmptyString(actorKind, 'actorKind');
    const normalizedMode = mode === undefined ? null : assertNonEmptyString(mode, 'mode');
    return [...this.actorStates.values()]
      .filter((state) => normalizedKind === null || state.actorKind === normalizedKind)
      .filter((state) => normalizedMode === null || state.mode === normalizedMode)
      .sort((left, right) => compareText(left.actorId, right.actorId));
  }

  removeActor(actorId) {
    const normalizedId = assertNonEmptyString(actorId, 'actorId');
    const state = this.actorStates.get(normalizedId) ?? null;
    if (!state) return null;
    this.actorStates.delete(normalizedId);
    this.revision += 1;
    return state;
  }

  beginStep({ tick, simulationTime, stepSeconds }) {
    this.currentStep = {
      tick: assertNonNegativeInteger(tick, 'tick'),
      simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
      stepSeconds: assertPositiveFinite(stepSeconds, 'stepSeconds'),
    };
    return this.getStateRef();
  }

  snapshot() {
    const payload = {
      version: TRAVERSAL_WORLD_VERSION,
      revision: this.revision,
      nextVolumeNumber: this.nextVolumeNumber,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      volumeCount: this.volumes.size,
      actorCount: this.actorStates.size,
      volumes: this.listVolumes().map((volume) => clone(volume)),
      actors: this.listActorStates().map((state) => clone(state)),
    };
    return deepFreeze({
      ...payload,
      checksum: computeTraversalWorldChecksum(payload),
    });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== TRAVERSAL_WORLD_VERSION) {
      throw new RangeError(`Unsupported traversal snapshot version: ${parsed.version}`);
    }
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const nextVolumeNumber = assertPositiveInteger(
      parsed.nextVolumeNumber,
      'snapshot.nextVolumeNumber',
    );
    const tick = parsed.tick === null ? null : assertNonNegativeInteger(parsed.tick, 'snapshot.tick');
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null
      ? null
      : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    if (!Array.isArray(parsed.volumes)) throw new TypeError('snapshot.volumes must be an array');
    if (!Array.isArray(parsed.actors)) throw new TypeError('snapshot.actors must be an array');
    const volumeCount = assertNonNegativeInteger(parsed.volumeCount, 'snapshot.volumeCount');
    const actorCount = assertNonNegativeInteger(parsed.actorCount, 'snapshot.actorCount');
    if (volumeCount !== parsed.volumes.length) {
      throw new RangeError('snapshot.volumeCount must match snapshot.volumes.length');
    }
    if (actorCount !== parsed.actors.length) {
      throw new RangeError('snapshot.actorCount must match snapshot.actors.length');
    }

    const volumes = parsed.volumes
      .map((volume) => buildVolume(volume))
      .sort((left, right) => compareText(left.id, right.id));
    if (new Set(volumes.map((volume) => volume.id)).size !== volumes.length) {
      throw new RangeError('snapshot contains duplicate traversal volume IDs');
    }
    if (volumes.some((volume) => volume.revision > revision)) {
      throw new RangeError('volume revision cannot exceed snapshot.revision');
    }
    const actors = parsed.actors
      .map((state) => buildActorState(state))
      .sort((left, right) => compareText(left.actorId, right.actorId));
    if (new Set(actors.map((state) => state.actorId)).size !== actors.length) {
      throw new RangeError('snapshot contains duplicate traversal actor IDs');
    }
    if (actors.some((state) => state.revision > revision)) {
      throw new RangeError('actor revision cannot exceed snapshot.revision');
    }
    const volumeIds = new Set(volumes.map((volume) => volume.id));
    for (const actor of actors) {
      for (const id of [
        ...actor.collisionVolumeIds,
        ...actor.contactVolumeIds,
        ...actor.hazardVolumeIds,
      ]) {
        if (!volumeIds.has(id)) throw new RangeError(`actor references missing volume: ${id}`);
      }
    }
    const generatedNumbers = volumes
      .map((volume) => /^volume-(\d+)$/.exec(volume.id))
      .filter(Boolean)
      .map((match) => Number(match[1]))
      .filter(Number.isSafeInteger);
    if (generatedNumbers.length > 0 && nextVolumeNumber <= Math.max(...generatedNumbers)) {
      throw new RangeError('snapshot.nextVolumeNumber must exceed generated volume IDs');
    }

    const payload = {
      version: TRAVERSAL_WORLD_VERSION,
      revision,
      nextVolumeNumber,
      tick,
      simulationTime,
      stepSeconds,
      volumeCount,
      actorCount,
      volumes: volumes.map((volume) => clone(volume)),
      actors: actors.map((state) => clone(state)),
    };
    if (typeof parsed.checksum !== 'string'
      || parsed.checksum !== computeTraversalWorldChecksum(payload)) {
      throw new RangeError('snapshot checksum is invalid');
    }

    this.volumes = new Map(volumes.map((volume) => [volume.id, volume]));
    this.actorStates = new Map(actors.map((state) => [state.actorId, state]));
    this.revision = revision;
    this.nextVolumeNumber = nextVolumeNumber;
    this.currentStep = { tick, simulationTime, stepSeconds };
    return this.snapshot();
  }

  reset() {
    this.volumes = new Map();
    this.actorStates = new Map();
    this.revision = 0;
    this.nextVolumeNumber = 1;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      volumeCount: snapshot.volumeCount,
      actorCount: snapshot.actorCount,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: TRAVERSAL_WORLD_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      nextVolumeNumber: this.nextVolumeNumber,
      volumeCount: this.volumes.size,
      actorCount: this.actorStates.size,
      kindCounts: Object.fromEntries(
        TRAVERSAL_VOLUME_KINDS.map((kind) => [kind, this.listVolumes({ kind }).length]),
      ),
    });
  }

  getDiagnostics() {
    return deepFreeze({
      status: this.getStatus(),
      volumes: this.listVolumes(),
      actors: this.listActorStates(),
    });
  }
}
